import { useSession } from "@d12labs/auth/client";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { navIcon } from "@d12labs/ui/sidebar-kit";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { pluginHomeTiles, pluginManifests } from "../../../plugins/registry";

/**
 * The app launcher (mounted at `/home`). Route-driven home: the sidebar shows
 * the launcher here, and these tiles jump into each plugin.
 */
export function HomePage() {
	const { data } = useSession();
	const firstName = (data?.user?.name ?? "").trim().split(/\s+/)[0];

	const homeTiles = Object.entries(pluginHomeTiles);

	return (
		<>
			<PageHeader
				eyebrow="Home"
				icon={Home}
				title={firstName ? `Welcome back, ${firstName}` : "Home"}
				subtitle="Pick up where you left off, or jump into any installed app."
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					{homeTiles.length > 0 && (
						<section className="mb-6">
							<h2 className="mb-3 card-title text-foreground">Your progress</h2>
							<div className="flex flex-col gap-3">
								{homeTiles.map(([name, Tile]) => (
									<Tile key={name} />
								))}
							</div>
						</section>
					)}
					<h2 className="mb-3 card-title text-foreground">Your apps</h2>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{pluginManifests.map((m) => {
							const href = m.navItems?.[0]?.href;
							if (!href) return null;
							const Icon = navIcon(m.navItems?.[0]?.icon);
							return (
								<Link
									key={m.name}
									to={href}
									className="group flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 p-3 transition-colors hover:border-hairline-strong hover:bg-surface-2"
								>
									<span className="flex size-9 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-muted transition-colors group-hover:border-primary/40 group-hover:text-primary">
										<Icon className="size-4" />
									</span>
									<div className="flex min-w-0 flex-1 flex-col">
										<span className="truncate text-body-sm font-medium text-foreground">
											{m.label}
										</span>
										<span className="truncate text-meta text-ink-subtle">{m.description}</span>
									</div>
									<ChevronRight className="size-4 flex-none text-ink-tertiary transition-colors group-hover:text-foreground" />
								</Link>
							);
						})}
					</div>
				</div>
			</PageBody>
		</>
	);
}
